<template>
  <div ref="host">
    <slot :compact="shownCompact"/>
  </div>
</template>

<script lang="ts">
import {Component, Vue, Prop, Setup, Watch, toNative} from 'vue-facing-decorator'
import {useTemplateRef} from 'vue'
//Da raiz, e não de '@vuetify/v0/composables' — ver o ViewTabs
import {useElementSize, UseElementSizeReturn} from '@vuetify/v0'

/**
 * Cabem todas as colunas na largura? Sim, a tabela; não, a visão compacta — a lista de itens, um
 * embaixo do outro. Rolar a tabela para o lado, nunca: se teria de rolar, é compacta (regra do
 * usuário). No telefone, compacta sempre (ver `shownCompact`). Era pela largura da tela (abaixo de 600px), e uma coleção de duas colunas virava lista à
 * toa no telefone, enquanto uma de dez rolava para o lado no computador.
 *
 * Quem desenha as duas formas é quem usa, pelo `compact` do slot. Aqui só se mede: com a tabela à
 * vista, ela não cabe quando o invólucro dela (`.v-table__wrapper`, o mesmo da v-table e da
 * v-data-table) rola para o lado; aí se guarda a largura que ela pedia, e a tabela só volta quando
 * a caixa chegar a essa largura — na compacta não há tabela para medir.
 *
 * A largura da caixa vem do `useElementSize` do Vuetify0, que se desliga sozinho. A medida roda
 * quando ela muda e quando o conteúdo muda (o `updated`: o slot é desenhado aqui, então os dados
 * dele são dependência deste componente) — antes da pintura, então a forma errada não chega a
 * aparecer.
 */
@Component({name: 'ontos-table-fit'})
class TableFit extends Vue {
  /**
   * Muda quando as colunas mudam: o que não cabia pode passar a caber, e a largura guardada já não
   * vale — volta à tabela para medir de novo.
   */
  @Prop({type: String, default: ''}) readonly columnsKey!: string

  @Setup(() => useElementSize(useTemplateRef('host')))
  readonly size!: UseElementSizeReturn

  compact = false
  /** A largura que a tabela pedia quando não coube. */
  private needed = 0
  /** O que a caixa tem a mais que o invólucro da tabela (recuos), para comparar uma com a outra. */
  private slack = 0

  /**
   * No telefone, sempre a compacta, caiba a tabela ou não (regra do usuário) — o mesmo corte de
   * telefone do resto do app (`xs`). Acima dele, decide a medida.
   */
  get shownCompact(): boolean {
    return this.$vuetify.display.xs || this.compact
  }

  get hostWidth(): number {
    return this.size.width.value
  }

  mounted() {
    this.fit()
  }

  updated() {
    this.fit()
  }

  @Watch('hostWidth')
  onHostWidth() {
    this.fit()
  }

  @Watch('columnsKey')
  onColumnsChange() {
    this.compact = false
  }

  fit() {
    const host = this.$refs.host as HTMLElement | undefined
    if (!host) return
    if (!this.compact) {
      const wrapper = host.querySelector('.v-table__wrapper') as HTMLElement | null
      if (wrapper && wrapper.scrollWidth > wrapper.clientWidth + 1) {
        this.needed = wrapper.scrollWidth
        this.slack = host.clientWidth - wrapper.clientWidth
        this.compact = true
      }
    } else if (host.clientWidth - this.slack >= this.needed) {
      //De volta à tabela; o `updated` mede de novo, para o caso de o conteúdo ter crescido
      this.compact = false
    }
  }
}

export default toNative(TableFit)
</script>
